// DEPENDENCIES
import axios from "axios";
import * as Sentry from "@sentry/react";
import {
  deleteField,
  doc,
  Firestore,
  getDoc,
  getFirestore,
  setDoc,
} from "@firebase/firestore";

// TYPES
import { BasicStep, ComponentStep, Step, Template } from "../../types";
import { AppDispatch, RootState } from "../../app/store";

// UTILS
import { getCompanyData } from "../../utils/companies";
import {
  deleteDniImages,
  deleteLiveness,
  genericToastifyError,
} from "../../utils";
import { store } from "../../app/store";
import { waitForFirebaseLogin } from "../../app/utils";
import { openModal } from "../../reducers/modalReducer";

// CONSTANTS
import { firestoreCollections } from "../../constants";

// STEPS
import identityStep from "../../steps/physical/identityStep";

// COMPONENTS
import CryptoAccountStep from "../../components/CryptoAccountStep/CryptoAccountStep";
import CryptoPhoneStep from "../../components/CryptoPhoneStep/CryptoPhoneStep";
import CryptoMissingDraft from "../../components/CryptoMissingDraft/CryptoMissingDraft";
import { Typography } from "@allaria-tech/matter/Typography";
import DniValidationErrorModal from "../../components/DniValidationErrorModal/DniValidationErrorModal";

// CONNECTORS
import {
  getCryptoLivenessSessionId,
  uploadDniCrypto,
  validateCryptoFace,
  validateDniCrypto,
  validateDniManualCrypto,
} from "../../connectors/connectors";

// REDUCERS
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import {
  getCryptoDraftIdFromUrl,
  getIsMobileLayoutFromUrl,
  postToParent,
} from "../../utils/crypto";

//STYLES
import style from "../../styles/crypto.module.scss";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";

const CUIT_MISMATCH_ERROR = "DNI_SCAN_FAILED_CUIT_MISMATCH";
const DNI_SCAN_FAILED_ERROR = "DNI_SCAN_FAILED";
const FACE_MISMATCH_MODAL = "modal-face-mismatch-error";
const LIVENESS_ERROR_MODAL = "modal-liveness-error";
const DNI_VALIDATION_ERROR_MODAL = "modal-dni-validation-error";

const handleClose = async () => postToParent("DIGITAL_ONBOARDING_CLOSE");

const handleBack = async () => postToParent("DIGITAL_ONBOARDING_BACK");

const getCryptoDniRef = ({ db, uuid }: { db: Firestore; uuid: string }) =>
  doc(db, firestoreCollections.dniCrypto, uuid);

let staleIdentityWipe: Promise<void> | null = null;

// Firestore keeps a draft's DNI and liveness documents until they expire, and
// the app may hand over a draft the user already went through: the identity
// step has to start from scratch, so they are dropped before it renders. Only
// on /digital and once per load, because the QR steps on the phone build this
// template too, and wiping from there would undo the desktop's progress.
const wipeStaleIdentity = () => {
  const uuid = getCryptoDraftIdFromUrl();
  if (window.location.pathname !== "/digital" || !uuid) return;

  staleIdentityWipe ??= Promise.all([
    deleteDniImages({ uuid, collection: firestoreCollections.dniCrypto }),
    deleteLiveness({ uuid, collection: firestoreCollections.livenessCrypto }),
  ])
    .then(() => undefined)
    .catch((error) => {
      Sentry.captureException(error);
    });

  return staleIdentityWipe;
};

// Without a draft there is nothing to validate the identity against, so the
// flow is replaced by an error the user can only close. Reported, since it means
// the app opened the onboarding without creating one first.
const missingDraftStep: Step = {
  step: 1,
  label: "cryptoMissingDraft",
  hideStepper: true,
  hideLayoutButtons: true,
  hideFromStepper: true,
  hideFormHeader: true,
  component: () => <CryptoMissingDraft />,
};

const reportMissingDraft = () => {
  // Logged as well as reported: on dev, where Sentry is off, this is the only
  // way to know why the onboarding did not start.
  console.warn("[crypto-onboarding] opened without a draft (uuid)");
  Sentry.captureMessage("Crypto onboarding opened without a draft", {
    level: "warning",
  });
};

// The identity substep each dot stands for: the QR (or scanner) and the front
// photo share the first; then the back photo, the manual DNI form, liveness.
const IDENTITY_DOTS: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 3, 5: 4 };

// Full screen in the app the gender stays a select, as in the phone design.
const withManualDniLayout = (
  subStep: BasicStep | ComponentStep,
): BasicStep | ComponentStep => {
  if (!("fields" in subStep) || !subStep.fields) return subStep;

  return {
    ...subStep,
    secondaryButton: { text: "Cerrar", onClick: handleClose },
    fields: subStep.fields.map((field) => ({
      ...field,
      columns: undefined,
      ...(field.name === "tramitNumber" && { infoTextType: "info" as const }),
      ...(field.name === "gender" &&
        getIsMobileLayoutFromUrl() !== true && {
          type: "segmented" as const,
          initialValue: "M",
        }),
    })),
  };
};

const onboardingCrypto = async (dispatch: AppDispatch): Promise<Template> => {
  const { formTitle, logo, favicon } = getCompanyData();

  // The QR steps on the phone carry their own uuid; only the app's /digital
  // depends on the one the app passes.
  const isMissingDraft =
    window.location.pathname === "/digital" && !getCryptoDraftIdFromUrl();
  if (isMissingDraft) reportMissingDraft();

  await wipeStaleIdentity();

  const identityCustomStep = identityStep([], {
    getDniRef: getCryptoDniRef,
    getLivenessRef: ({ db, uuid }) =>
      doc(db, firestoreCollections.livenessCrypto, uuid),

    // DNI
    editableIdentityDocument: true,
    uploadDniSubtitle: (
      <div className={style.identitySubtitleContainer}>
        <p>Es necesario validar tu identidad.</p>
        <p>Cargá fotos del frente y dorso de tu DNI.</p>
      </div>
    ),
    uploadDniQrDescription: (
      <div className={style.qrDescription}>
        <Typography
          type="BODY"
          size="M"
          color="var(--typography-body-color-base)"
        >
          Vamos a verificar tu DNI.
        </Typography>
        <Typography
          type="BODY"
          size="M"
          color="var(--typography-body-color-base)"
        >
          Escaneá el código QR para continuar desde tu celular:
        </Typography>
      </div>
    ),
    // The DNI opens the flow: its first screen takes the user back to the
    // app's previous steps instead of closing the onboarding.
    onCloseUploadDni: handleBack,
    uploadDniCloseText: "Volver",
    stepUploadModal: (
      <DniValidationErrorModal
        title="Error en la validación"
        description="El documento no coincide con el titular de la cuenta."
        modalName={DNI_VALIDATION_ERROR_MODAL}
        onClose={handleClose}
      />
    ),
    onCustomScanDni: async (front, back, uuid, dniRef) => {
      const { data } = await validateDniCrypto({
        draftId: uuid,
        frontImageBase64: front.base64,
        backImageBase64: back.base64,
      });

      const details = data?.details ? JSON.parse(data.details) : null;

      if (data?.result !== "OK") {
        if (details?.error?.includes("CUIT mismatch")) {
          throw new Error(CUIT_MISMATCH_ERROR);
        }
        throw new Error(DNI_SCAN_FAILED_ERROR);
      }

      if (details?.dniData) {
        dispatch(
          onUpdateFormData({
            identityDocument: details.dniData.number,
            gender: details.dniData.gender,
          }),
        );
      }

      await setDoc(
        dniRef,
        {
          frontDNI: front.name,
          backDNI: back.name,
          dniScanValidation: data.result,
          remoteToken: deleteField(),
        },
        { merge: true },
      );
    },
    onCustomScanError: (error) => {
      if (error instanceof Error) {
        if (error.message === CUIT_MISMATCH_ERROR) {
          dispatch(openModal({ name: DNI_VALIDATION_ERROR_MODAL }));
        } else {
          showToastMessage(
            <span>
              Volvé a cargar la foto del DNI asegurándote de que esté bien
              iluminada, sin reflejos y que todos los datos se vean claramente.
            </span>,
            { containerId: "formAlert", type: "ERROR" },
          );
        }
      }
    },
    onUploadDniFallback: async (front, back, uuid) => {
      await uploadDniCrypto({
        draftId: uuid,
        frontImageBase64: front.base64,
        backImageBase64: back.base64,
      });
    },
    onUploadDniFallbackError: () =>
      showToastMessage(genericToastifyError, {
        containerId: "formAlert",
        type: "ERROR",
      }),
    // Read from Firestore, not the store: scanned from the phone via the QR,
    // the result was saved there and never reached this tab's store. If it
    // cannot be read, the manual form still validates the DNI, instead of
    // leaving the QR screen stuck.
    onContinueUploadDni: async (_values, _dispatch, state) => {
      await waitForFirebaseLogin();
      const goToLiveness = await getDoc(
        getCryptoDniRef({
          db: getFirestore(),
          uuid: state.onboardingData.draftUUID,
        }),
      )
        .then((dniDoc) => dniDoc.data()?.dniScanValidation === "OK")
        .catch((error) => {
          Sentry.captureException(error);
          return false;
        });

      return {
        nextSubStep: goToLiveness ? 5 : 4,
      };
    },

    // DNI MANUAL FORM
    onContinueDniManualForm: async (values) => {
      await validateDniManualCrypto({
        draftId: store.getState().onboardingData.draftUUID,
        dniNumber: values.identityDocument,
        gender: values.gender,
        order: values.tramitNumber,
      });
    },

    // LIVENESS
    stepLivenessModal: (
      <>
        <DniValidationErrorModal
          title="Error en la validación"
          description="La validación de identidad no coincide con el documento del titular de la cuenta."
          modalName={FACE_MISMATCH_MODAL}
          onClose={handleClose}
        />
        <DniValidationErrorModal
          title="Algo salió mal"
          description="Por favor, intentá nuevamente."
          modalName={LIVENESS_ERROR_MODAL}
          onClose={handleClose}
        />
      </>
    ),
    onCustomGetSessionId: (uuid) =>
      getCryptoLivenessSessionId({ draftId: uuid }),
    onCustomValidateFace: async (uuid, sessionId, livenessRef) => {
      const { data } = await validateCryptoFace({
        draftId: uuid,
        sessionId,
      });
      if (data.result === "OK") {
        await setDoc(
          livenessRef,
          {
            isValid: true,
            userAgentData: window.navigator.userAgent,
            remoteToken: deleteField(),
          },
          { merge: true },
        );
      } else {
        throw new Error("FACE_VALIDATION_FAILED");
      }
    },
    onCustomValidateFaceError: (error, reachedAttemptsLimit) => {
      if (reachedAttemptsLimit) return;
      if (
        axios.isAxiosError(error) &&
        error.response?.data?.error_code === "face_mismatch"
      ) {
        dispatch(openModal({ name: FACE_MISMATCH_MODAL }));
      } else {
        dispatch(openModal({ name: LIVENESS_ERROR_MODAL }));
      }
    },
    livenessDescription: (
      <div className={style.livenessDescription}>
        <Typography
          type="BODY"
          size="L"
          weight="EMPHASIS"
          color="var(--typography-body-color-base)"
        >
          A continuación debés validar tu identidad
        </Typography>
        <Typography
          type="BODY"
          size="M"
          color="var(--typography-body-color-base)"
        >
          Asegurate de que la foto del dorso sea clara:
        </Typography>
      </div>
    ),
    onCloseLiveness: handleClose,
  });

  return {
    onboardingType: "DIGITAL",
    theme: "DIGITAL",
    dotsDisabled: true,
    formTitle,
    logo,
    favicon,
    steps: isMissingDraft
      ? [missingDraftStep]
      : [
          {
            step: 1,
            label: "cryptoIdentity",
            hideFromStepper: true,
            // Its empty column would push the identity screens 1.5rem down.
            hideStepper: true,
            ...identityCustomStep,
            // The modal is laid out as a phone, where the identity step hides its
            // form header: here it carries the identity screens' title.
            hideFormHeaderOnMobile: false,
            subSteps: identityCustomStep.subSteps?.map((subStep) =>
              subStep.label === "uploadDNI" ||
              subStep.label === "livenessValidation" ||
              [2, 3, 4].includes(Number(subStep.step))
                ? {
                    ...(Number(subStep.step) === 4
                      ? withManualDniLayout(subStep)
                      : subStep),
                    titleOfStep: "Identidad",
                    titleColor: "var(--primary-300)",
                    hideStepper: true,
                    dotStep: {
                      current: IDENTITY_DOTS[Number(subStep.step)],
                      // The fifth dot is the phone step that follows.
                      total: 5,
                    },
                    // Full screen the scanner's own heading opens the step.
                    ...(subStep.label === "uploadDNI" && {
                      hideFormHeader: (state: RootState) =>
                        state.crypto.isMobileLayout === true,
                    }),
                  }
                : { ...subStep, hideFormHeader: true },
            ),
          },
          {
            step: 2,
            label: "cryptoPhone",
            hideStepper: true,
            hideLayoutButtons: true,
            hideFromStepper: true,
            component: (dispatch, state, formik) => (
              <CryptoPhoneStep
                dispatch={dispatch}
                state={state}
                formik={formik}
              />
            ),
          },
          {
            step: 3,
            label: "cryptoAccount",
            hideStepper: true,
            hideLayoutButtons: true,
            hideFromStepper: true,
            component: (dispatch, state, formik) => (
              <CryptoAccountStep
                dispatch={dispatch}
                state={state}
                formik={formik}
              />
            ),
            // CryptoAccountStep sends the account data and completes the
            // onboarding itself, so a failure can offer to retry in a modal.
          },
        ],
    remoteSteps: [
      {
        link: "/dni",
        step: identityCustomStep.subSteps?.find(
          ({ label }) => label === "uploadDNI",
        ) as ComponentStep,
      },
      {
        link: "/identidad",
        step: identityCustomStep.subSteps?.find(
          ({ label }) => label === "livenessValidation",
        ) as ComponentStep,
      },
    ],
    preconfirmation: null,
    hideLogo: true,
    hideSupportButton: true,
    buttonsInsideContainer: true,
    customButton: {
      text: "Cerrar",
      onClick: handleClose,
    },
  };
};

export default onboardingCrypto;
